import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, request } from '@starling/core/api/client';
import { useAuthStore } from '@starling/core/auth/store';
import type { components } from '@/api/types.gen';
import type { components as m1Components } from '@/api/types.m1.gen';

// Реклама Starling Vision (V1.4): лента баннеров на главной, заявка от
// verified-организации, «Моя реклама», загрузка баннера в платформенные медиа.
// Контракт: docs/api/openapi-vision.yaml (тег vision/ads, platform/media),
// тариф — openapi-m1.yaml (`GET /apps/{app}/pricing`, публичные тарифы).

export type AdPublic = components['schemas']['AdPublic'];
export type AdFeed = components['schemas']['AdFeed'];
export type AdCreate = components['schemas']['AdCreate'];
export type AdMine = components['schemas']['AdMine'];
export type AdMineList = components['schemas']['AdMineList'];
export type AdStatus = components['schemas']['AdStatus'];
export type MediaImage = components['schemas']['MediaImage'];
export type PriceItem = m1Components['schemas']['PriceItem'];
export type PricingList = m1Components['schemas']['PricingList'];

/** Все статусы заявки — exhaustive-список для бейджей/переводов. */
export const AD_STATUSES: readonly AdStatus[] = [
  'pending',
  'approved',
  'active',
  'expired',
  'stopped',
  'rejected',
  'withdrawn',
];

/** Допустимые сроки показа (enum контракта). */
export const AD_WEEKS = [1, 2, 4] as const;
export type AdWeeks = (typeof AD_WEEKS)[number];

/** Код услуги «реклама за неделю показа» в тарифах Vision. */
export const AD_WEEK_CODE = 'ad_week';

export const ADS_KEY = ['vision', 'ads'] as const;
export const MY_ADS_KEY = ['vision', 'my', 'ads'] as const;
export const VISION_PRICING_KEY = ['apps', 'vision', 'pricing'] as const;

/**
 * Лента баннеров на главной (`GET /vision/ads`, без токена). Каждый вызов
 * засчитывает показ на бэке — поэтому кэш длинный и refetch по фокусу окна
 * выключен глобально (App.tsx): счётчик не должен накручиваться от навигации.
 */
export function useAdsQuery() {
  return useQuery({
    queryKey: ADS_KEY,
    queryFn: () => api.get<AdFeed>('/api/v1/vision/ads'),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Публичные тарифы Vision (`GET /apps/vision/pricing`, без токена) — только
 * включённые услуги. Нужен тариф `ad_week`: сумма «= N ₸» в форме и
 * `payment_note` в блоке «Ожидает оплаты» «Моей рекламы».
 */
export function useVisionPricingQuery() {
  return useQuery({
    queryKey: VISION_PRICING_KEY,
    queryFn: () => api.get<PricingList>('/api/v1/apps/vision/pricing'),
    staleTime: 5 * 60 * 1000,
  });
}

/** Тариф рекламы из публичного списка; undefined — услуга выключена/не заведена. */
export function findAdWeekPrice(pricing: PricingList | undefined): PriceItem | undefined {
  return pricing?.items.find((item) => item.code === AD_WEEK_CODE && item.enabled);
}

/** «Мои заявки» (`GET /vision/my/ads`) — все организации, все статусы, свежие сверху. */
export function useMyAdsQuery() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  return useQuery({
    queryKey: MY_ADS_KEY,
    queryFn: () => api.get<AdMineList>('/api/v1/vision/my/ads'),
    enabled: isAuthenticated,
  });
}

/** Подать заявку (`POST /vision/ads`, 201 → pending). После успеха — «Моя реклама» перечитается. */
export function useCreateAdMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['vision', 'ads', 'create'],
    mutationFn: (input: AdCreate) => api.post<AdMine>('/api/v1/vision/ads', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: MY_ADS_KEY });
    },
  });
}

/**
 * Отозвать заявку (`POST /vision/my/ads/{id}/withdraw`, pending|approved →
 * withdrawn). 409 `ad_not_withdrawable` — статус успел смениться: список
 * перечитываем в любом исходе, чтобы карточка показала актуальное.
 */
export function useWithdrawAdMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['vision', 'ads', 'withdraw'],
    mutationFn: (adId: string) => api.post<AdMine>(`/api/v1/vision/my/ads/${adId}/withdraw`),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: MY_ADS_KEY });
    },
  });
}

// ── Загрузка баннера ────────────────────────────────────────────────────────

/** MIME-типы, которые принимает `POST /media/images` (проверка по содержимому на бэке). */
export const IMAGE_ACCEPT = 'image/jpeg,image/png,image/webp';

const MEDIA_IMAGES_URL = '/api/v1/media/images';

/**
 * `POST /media/images` — multipart (`file` + `kind=ad_banner`). Общий клиент
 * `@starling/core` умеет только JSON-тело (`jsonBody`), поэтому здесь — явный
 * `fetch` с тем же Authorization; ошибки приводятся к `ApiError` того же
 * envelope-формата, чтобы форма разбирала 413/415/422 единообразно.
 * Авто-refresh сессии по 401 здесь не делаем: загрузка идёт из уже открытой
 * формы, чей GET организаций refresh уже выполнил при необходимости.
 */
export async function uploadImage(file: File, kind: MediaImage['kind'] = 'ad_banner') {
  const form = new FormData();
  form.append('file', file);
  form.append('kind', kind);
  // Через общий клиент (formBody, V1.4): тот же Authorization и refresh по 401 —
  // отдельный fetch без refresh давал «не удалось загрузить» на истёкшем токене.
  return request<MediaImage>(MEDIA_IMAGES_URL, { method: 'POST', formBody: form });
}

export function useUploadImageMutation() {
  return useMutation({
    mutationKey: ['media', 'images', 'upload'],
    mutationFn: (file: File) => uploadImage(file),
  });
}
